function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
var _handleResize = _classPrivateFieldLooseKey("handleResize");
var _previousScrollY = _classPrivateFieldLooseKey("previousScrollY");
var _handleClick = _classPrivateFieldLooseKey("handleClick");
var _handleKeyDown = _classPrivateFieldLooseKey("handleKeyDown");
export class DialogComponent extends Component {
  constructor(...args) {
    super(...args);
    /**
     * Closes the dialog when the user presses the escape key.
     *
     * @param {KeyboardEvent} event - The keyboard event.
     */
    Object.defineProperty(this, _handleKeyDown, {
      value: _handleKeyDown2
    });
    /**
     * Closes the dialog when the user clicks outside of it.
     *
     * @param {MouseEvent} event - The mouse event.
     */
    Object.defineProperty(this, _handleClick, {
      value: _handleClick2
    });
    this.requiredRefs = ['dialog'];
    Object.defineProperty(this, _handleResize, {
      writable: true,
      value: debounce(() => {
        const {
          minWidth,
          maxWidth
        } = this;
        if (!minWidth && !maxWidth) return;
        const windowWidth = window.innerWidth;
        if (windowWidth < minWidth || windowWidth > maxWidth) {
          this.closeDialog();
        }
      }, 50)
    });
    Object.defineProperty(this, _previousScrollY, {
      writable: true,
      value: 0
    });
    /**
     * Closes the dialog.
     */
    this.closeDialog = async () => {
      const {
        dialog
      } = this.refs;
      if (!dialog.open) return;
      this.removeEventListener('click', _classPrivateFieldLooseBase(this, _handleClick)[_handleClick]);
      this.removeEventListener('keydown', _classPrivateFieldLooseBase(this, _handleKeyDown)[_handleKeyDown]);

      // Force browser to restart animation by resetting it
      // Temporarily remove any existing animation state
      dialog.style.animation = 'none';

      // Force a reflow
      void dialog.offsetWidth;

      // Now add the closing class and restore animation
      dialog.classList.add('dialog-closing');
      dialog.style.animation = '';
      await onAnimationEnd(dialog, undefined, {
        subtree: false
      });
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      window.scrollTo({
        top: _classPrivateFieldLooseBase(this, _previousScrollY)[_previousScrollY],
        behavior: 'instant'
      });
      dialog.close();
      dialog.classList.remove('dialog-closing');
      this.dispatchEvent(new DialogCloseEvent());
    };
    /**
     * Toggles the dialog.
     */
    this.toggleDialog = () => {
      if (this.refs.dialog.open) {
        this.closeDialog();
      } else {
        this.showDialog();
      }
    };
  }
  connectedCallback() {
    super.connectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', _classPrivateFieldLooseBase(this, _handleResize)[_handleResize]);
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', _classPrivateFieldLooseBase(this, _handleResize)[_handleResize]);
    }
  }
  /**
   * Shows the dialog.
   */
  showDialog() {
    const {
      dialog
    } = this.refs;
    if (dialog.open) return;
    const scrollY = window.scrollY;
    _classPrivateFieldLooseBase(this, _previousScrollY)[_previousScrollY] = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      this.addEventListener('click', _classPrivateFieldLooseBase(this, _handleClick)[_handleClick]);
      this.addEventListener('keydown', _classPrivateFieldLooseBase(this, _handleKeyDown)[_handleKeyDown]);
    });
  }
  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}
function _handleClick2(event) {
  const {
    dialog
  } = this.refs;
  if (isClickedOutside(event, dialog)) {
    this.closeDialog();
  }
}
function _handleKeyDown2(event) {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  this.closeDialog();
}
if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);
export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }
}
DialogOpenEvent.eventName = 'dialog:open';
export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }
}
DialogCloseEvent.eventName = 'dialog:close';
document.addEventListener('toggle', event => {
  if (event.target instanceof HTMLDetailsElement) {
    if (event.target.hasAttribute('scroll-lock')) {
      const {
        open
      } = event.target;
      if (open) {
        document.documentElement.setAttribute('scroll-lock', '');
      } else {
        document.documentElement.removeAttribute('scroll-lock');
      }
    }
  }
}, {
  capture: true
});
